// Hero — premium 3D cup (with real GLAMM logo embossed), realistic floating
// coffee beans, layered animated orbs, mouse + scroll parallax.

import { useEffect, useMemo, useState } from 'react';
import { useContent, useLang, TONE_BG, UI } from './content.jsx';
import { Reveal, PulseDot } from './primitives.jsx';
import { RevealText, Counter } from './animations.jsx';

/* ===================== Beans ===================== */

export function Bean({ size = 28, rotate = 0, variant = 0, style }) {
  const id = `b${variant}`;
  return (
    <svg width={size} height={size * 1.4} viewBox="0 0 40 56"
      style={{ ...style, transform: `rotate(${rotate}deg)`, overflow: 'visible' }}>
      <defs>
        <radialGradient id={`${id}-body`} cx="0.32" cy="0.28" r="0.95">
          <stop offset="0"    stopColor="#a67148"/>
          <stop offset="0.22" stopColor="#7b4a2a"/>
          <stop offset="0.55" stopColor="#4a2814"/>
          <stop offset="1"    stopColor="#1a0a02"/>
        </radialGradient>
        <linearGradient id={`${id}-crease`} x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0"   stopColor="rgba(0,0,0,0.85)"/>
          <stop offset="0.5" stopColor="rgba(0,0,0,0.55)"/>
          <stop offset="1"   stopColor="rgba(0,0,0,0.85)"/>
        </linearGradient>
        <radialGradient id={`${id}-sheen`} cx="0.3" cy="0.25" r="0.35">
          <stop offset="0" stopColor="rgba(255,221,176,0.85)"/>
          <stop offset="1" stopColor="rgba(255,221,176,0)"/>
        </radialGradient>
      </defs>
      <ellipse cx="22" cy="52" rx="11" ry="2.2" fill="rgba(0,0,0,0.28)" filter="blur(1.5px)"/>
      <ellipse cx="20" cy="28" rx="14" ry="24" fill={`url(#${id}-body)`}/>
      <path d="M32 6 C 36 18, 36 40, 32 50 C 30 46, 30 10, 32 6 Z" fill="rgba(0,0,0,0.35)"/>
      <path d="M20 6 C 14 20, 14 36, 20 50 C 19 40, 19 16, 20 6 Z" fill={`url(#${id}-crease)`}/>
      <path d="M20 8 C 15 20, 15 36, 20 48"
        stroke="#0a0502" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.9"/>
      <ellipse cx="12" cy="16" rx="7" ry="12" fill={`url(#${id}-sheen)`}/>
      <ellipse cx="10" cy="12" rx="1.8" ry="3.5" fill="rgba(255,240,210,0.9)"/>
    </svg>
  );
}

export function FloatingBeans({ count = 18 }) {
  const beans = useMemo(() => Array.from({ length: count }).map((_, i) => ({
    id: i,
    top: Math.random() * 92 + '%', left: Math.random() * 96 + '%',
    tx: (Math.random() * 80 - 40) + 'px', ty: (Math.random() * -120 - 20) + 'px',
    r: Math.floor(Math.random() * 360),
    dur: (Math.random() * 12 + 10) + 's',
    op: (Math.random() * 0.32 + 0.14),
    size: Math.floor(Math.random() * 18) + 18,
    v: i % 3,
    delay: (Math.random() * -10) + 's',
  })), [count]);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {beans.map(b => (
        <div key={b.id} className="bean" style={{
          position: 'absolute',
          top: b.top, left: b.left,
          '--tx': b.tx, '--ty': b.ty, '--r': b.r + 'deg',
          '--dur': b.dur, '--op': b.op,
          animationDelay: b.delay,
          opacity: b.op,
        }}>
          <Bean size={b.size} rotate={b.r} variant={b.v}/>
        </div>
      ))}
    </div>
  );
}

/* ===================== Cup ===================== */

export function LatteCup({ size = 540, speed = 18, showLogo = true, style = 'porcelain', logoSrc = '/assets/glamm.png' }) {
  const bodyLight =
    style === 'matte' ? ['#d8c5a3', '#f0e3c9', '#f5ebd6', '#ead3aa', '#b78e5a'] :
    style === 'ceramic' ? ['#e0caa4', '#f4e6c8', '#faf1dd', '#e1c396', '#8a5a38'] :
                          ['#e8d4b4', '#fbf3e3', '#ffffff', '#faf1dd', '#8b5a3c'];

  const swayKey = `cupSway_${speed}`;

  return (
    <div className="relative nosel" style={{ width: size, height: size }}>
      <style>{`
        @keyframes cupBreath { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
        .cup-breathe { animation: cupBreath 6s ease-in-out infinite }
        @keyframes ${swayKey} { 0%,100% { transform: rotate(-2deg) } 50% { transform: rotate(2deg) } }
        .cup-sway-${speed} { animation: ${swayKey} ${speed}s ease-in-out infinite }
      `}</style>

      <div className="cup-breathe">
        <div className={`cup-sway-${speed}`}>
          <svg viewBox="0 0 540 600" className="w-full h-full"
            style={{ filter: 'drop-shadow(0 36px 52px rgba(74,46,30,0.32))' }}>
            <defs>
              <linearGradient id="mugBody" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0"    stopColor={bodyLight[0]}/>
                <stop offset="0.12" stopColor={bodyLight[1]}/>
                <stop offset="0.35" stopColor={bodyLight[2]}/>
                <stop offset="0.58" stopColor={bodyLight[3]}/>
                <stop offset="0.82" stopColor={bodyLight[4] || '#d8bd95'}/>
                <stop offset="1"    stopColor="#8b5a3c"/>
              </linearGradient>
              <linearGradient id="mugVert" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0"    stopColor="rgba(255,255,255,0)"/>
                <stop offset="0.12" stopColor="rgba(255,255,255,0.4)"/>
                <stop offset="0.5"  stopColor="rgba(255,255,255,0)"/>
                <stop offset="1"    stopColor="rgba(74,46,30,0.2)"/>
              </linearGradient>
              <linearGradient id="rimTop" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0"   stopColor="#c9a575"/>
                <stop offset="0.4" stopColor="#ffffff"/>
                <stop offset="0.7" stopColor="#faf1dd"/>
                <stop offset="1"   stopColor="#8b5a3c"/>
              </linearGradient>
              <radialGradient id="mugInside" cx="0.45" cy="0.2" r="0.8">
                <stop offset="0"   stopColor="#3d2618"/>
                <stop offset="0.6" stopColor="#2a1a10"/>
                <stop offset="1"   stopColor="#1a0e06"/>
              </radialGradient>
              <radialGradient id="coffeeTop" cx="0.4" cy="0.35" r="0.8">
                <stop offset="0"   stopColor="#e4b578"/>
                <stop offset="0.3" stopColor="#a0744a"/>
                <stop offset="0.8" stopColor="#4a2814"/>
                <stop offset="1"   stopColor="#1a0e06"/>
              </radialGradient>
              <radialGradient id="cremaG" cx="0.5" cy="0.45" r="0.55">
                <stop offset="0"   stopColor="#fbf6ec"/>
                <stop offset="0.6" stopColor="#f0dec0"/>
                <stop offset="1"   stopColor="#d9b87e"/>
              </radialGradient>
              <radialGradient id="saucerTop" cx="0.5" cy="0.35" r="0.7">
                <stop offset="0"   stopColor="#ffffff"/>
                <stop offset="0.6" stopColor="#f6ecd9"/>
                <stop offset="1"   stopColor="#c9a575"/>
              </radialGradient>
              <linearGradient id="handleMain" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0"    stopColor="#d9bf93"/>
                <stop offset="0.45" stopColor="#fbf3e3"/>
                <stop offset="1"    stopColor="#8b5a3c"/>
              </linearGradient>
              <radialGradient id="cupHalo" cx="0.5" cy="0.5" r="0.6">
                <stop offset="0" stopColor="rgba(245,220,212,0.7)"/>
                <stop offset="1" stopColor="rgba(245,220,212,0)"/>
              </radialGradient>
              <filter id="cupBlur" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="4"/>
              </filter>
              <filter id="softInner" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2"/>
              </filter>
              <clipPath id="mugFrontClip">
                <path d="M 130 210
                         C 128 220, 126 240, 126 262
                         C 126 338, 140 414, 172 446
                         L 368 446
                         C 400 414, 414 338, 414 262
                         C 414 240, 412 220, 410 210 Z"/>
              </clipPath>
              <filter id="embossFx" x="-5%" y="-5%" width="110%" height="110%">
                <feGaussianBlur stdDeviation="0.6" result="blur"/>
                <feSpecularLighting result="spec" in="blur"
                  specularExponent="28" lightingColor="#ffffff" surfaceScale="2">
                  <feDistantLight azimuth="135" elevation="55"/>
                </feSpecularLighting>
                <feComposite in="spec" in2="SourceAlpha" operator="in" result="specOut"/>
                <feComposite in="SourceGraphic" in2="specOut" operator="arithmetic" k1="0" k2="1" k3="1" k4="0"/>
              </filter>
            </defs>

            <circle cx="270" cy="290" r="240" fill="url(#cupHalo)"/>

            {/* Saucer */}
            <ellipse cx="270" cy="468" rx="228" ry="44" fill="url(#saucerTop)" stroke="#b89060" strokeWidth="1.4" opacity="0.96"/>
            <ellipse cx="270" cy="466" rx="196" ry="34" fill="none" stroke="rgba(139,90,60,0.22)" strokeWidth="1"/>
            <ellipse cx="270" cy="460" rx="134" ry="22" fill="rgba(139,90,60,0.16)"/>
            <path d="M 62 460 A 228 44 0 0 1 478 460" fill="none" stroke="#ffffff" strokeWidth="1.4" opacity="0.7"/>

            {/* Handle */}
            <g>
              <path d="M 414 252 C 500 240, 516 316, 500 370 C 484 420, 446 428, 416 414"
                fill="none" stroke="rgba(74,46,30,0.4)" strokeWidth="36" strokeLinecap="round"/>
              <path d="M 414 252 C 500 240, 516 316, 500 370 C 484 420, 446 428, 416 414"
                fill="none" stroke="url(#handleMain)" strokeWidth="30" strokeLinecap="round"/>
              <path d="M 418 262 C 490 252, 506 312, 494 360"
                fill="none" stroke="#ffffff" strokeWidth="5.5" strokeLinecap="round" opacity="0.7"/>
              <path d="M 422 274 C 482 268, 496 316, 480 354 C 470 380, 448 388, 426 380"
                fill="none" stroke="rgba(139,90,60,0.55)" strokeWidth="2.5" strokeLinecap="round"/>
            </g>

            {/* Cup body */}
            <path d="M 130 210
                     L 410 210
                     C 414 218, 416 240, 416 262
                     C 416 342, 402 422, 372 452
                     L 168 452
                     C 138 422, 124 342, 124 262
                     C 124 240, 126 218, 130 210 Z"
              fill="url(#mugBody)"/>
            <path d="M 130 210
                     L 410 210
                     C 414 218, 416 240, 416 262
                     C 416 342, 402 422, 372 452
                     L 168 452
                     C 138 422, 124 342, 124 262
                     C 124 240, 126 218, 130 210 Z"
              fill="url(#mugVert)"/>

            <path d="M 158 222 C 156 290, 162 380, 180 436"
              fill="none" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" opacity="0.85" filter="url(#cupBlur)"/>
            <path d="M 398 220 C 402 300, 390 406, 368 442"
              fill="none" stroke="rgba(74,46,30,0.22)" strokeWidth="18" strokeLinecap="round" opacity="0.6"/>

            {/* GLAMM logo on the mug front — clean transparent PNG, multiply-blended for stamp effect */}
            {showLogo && (
              <g clipPath="url(#mugFrontClip)">
                <image href={logoSrc}
                  x="190" y="278" width="160" height="160"
                  preserveAspectRatio="xMidYMid meet"
                  opacity="0.82"
                  style={{ mixBlendMode: 'multiply' }}/>
              </g>
            )}

            {/* Inside rim */}
            <ellipse cx="270" cy="214" rx="142" ry="24" fill="url(#mugInside)"/>
            {/* Coffee surface */}
            <ellipse cx="270" cy="209" rx="138" ry="21" fill="url(#coffeeTop)"/>

            {/* Crema rosetta */}
            <g transform="translate(270, 209)">
              <ellipse rx="120" ry="16" fill="url(#cremaG)" opacity="0.22"/>
              <path d="M -2 -18 C -4 -4, -4 6, -2 18 C 2 6, 2 -4, 2 -18 Z" fill="url(#cremaG)" opacity="0.95"/>
              {[0,1,2,3].map(i => {
                const yy = -14 + i * 9;
                const w = 32 - i * 5;
                const h = 4;
                return (
                  <path key={i}
                    d={`M ${-w} ${yy} C ${-w*0.3} ${yy - h}, ${w*0.3} ${yy - h}, ${w} ${yy}
                        C ${w*0.3} ${yy + h}, ${-w*0.3} ${yy + h}, ${-w} ${yy} Z`}
                    fill="url(#cremaG)" opacity="0.92"/>
                );
              })}
              <path d="M 0 18 C -9 13, -12 6, -5 5 C -2 4.5, 0 7, 0 10 C 0 7, 2 4.5, 5 5 C 12 6, 9 13, 0 18 Z"
                fill="url(#cremaG)" opacity="0.95"/>
            </g>

            <path d="M 128 210 A 142 24 0 0 1 412 210" fill="none" stroke="url(#rimTop)" strokeWidth="4.5"/>
            <ellipse cx="210" cy="202" rx="26" ry="2" fill="#ffffff" opacity="0.75" filter="url(#cupBlur)"/>

            <ellipse cx="188" cy="308" rx="20" ry="64" fill="rgba(255,255,255,0.5)" filter="url(#cupBlur)"/>
            <ellipse cx="368" cy="308" rx="10" ry="44" fill="rgba(255,255,255,0.22)" filter="url(#cupBlur)"/>

            <ellipse cx="270" cy="456" rx="118" ry="10" fill="rgba(74,46,30,0.36)" filter="url(#cupBlur)"/>

            <g transform="translate(470,488)" opacity="0.7">
              <path d="M 0 6 C -6 2, -8 -4, -3 -5 C -1 -5, 0 -3, 0 -1 C 0 -3, 1 -5, 3 -5 C 8 -4, 6 2, 0 6 Z" fill="#e9b7a6"/>
            </g>
          </svg>
        </div>
      </div>

      {/* Steam — layered turbulent puffs */}
      <div className="absolute inset-0 pointer-events-none" style={{ top: '-12%' }}>
        {[
          { left: '46%', delay: '0s',   top: '26%', size: 52, sway: [10, -8, 18],   dur: '5.4s' },
          { left: '50%', delay: '0.5s', top: '24%', size: 64, sway: [-12, 14, -6],  dur: '6.2s' },
          { left: '54%', delay: '1.0s', top: '27%', size: 48, sway: [14, -6, 24],   dur: '5.0s' },
          { left: '48%', delay: '1.5s', top: '22%', size: 58, sway: [-8, 12, -16],  dur: '5.8s' },
          { left: '52%', delay: '2.0s', top: '25%', size: 42, sway: [8, -14, 10],   dur: '4.8s' },
          { left: '56%', delay: '2.5s', top: '23%', size: 54, sway: [-14, 8, -12],  dur: '5.6s' },
          { left: '44%', delay: '3.0s', top: '28%', size: 38, sway: [6, -10, 16],   dur: '4.6s' },
          { left: '50%', delay: '3.5s', top: '26%', size: 60, sway: [-10, 16, -4],  dur: '6.0s' },
          { left: '47%', delay: '0.8s', top: '23%', size: 34, sway: [12, -8, 20],   dur: '5.2s' },
          { left: '53%', delay: '1.2s', top: '29%', size: 46, sway: [-6, 10, -18],  dur: '5.4s' },
          { left: '55%', delay: '2.8s', top: '21%', size: 40, sway: [16, -12, 8],   dur: '4.9s' },
          { left: '49%', delay: '4.0s', top: '27%', size: 50, sway: [-16, 6, -14],  dur: '5.7s' },
        ].map((c, i) => (
          <span key={i} className="steam-wisp" style={{
            left: c.left, top: c.top,
            width: c.size, height: c.size,
            animationDelay: c.delay,
            '--sway1': `${c.sway[0]}px`,
            '--sway2': `${c.sway[1]}px`,
            '--sway3': `${c.sway[2]}px`,
            '--dur': c.dur,
          }}/>
        ))}
      </div>
    </div>
  );
}

/* ===================== Hero ===================== */

export function Hero({ onOpenMenu, onOpenStores }) {
  const c = useContent();
  const { t } = useLang();
  const h = c.hero;
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
  const isMobile = vw < 768;
  // Desktop/tablet: fixed 540 like before. Mobile: scale to viewport, cap at 380.
  const cupSize = isMobile ? Math.min(380, Math.round(vw * 0.82)) : 540;
  const parallaxX = isMobile ? 0 : mouse.x;
  const parallaxY = isMobile ? 0 : mouse.y;

  useEffect(() => {
    const f = (e) => {
      const { innerWidth: w, innerHeight: h } = window;
      setMouse({ x: (e.clientX / w - 0.5) * 2, y: (e.clientY / h - 0.5) * 2 });
    };
    window.addEventListener('mousemove', f);
    return () => window.removeEventListener('mousemove', f);
  }, []);
  useEffect(() => {
    const f = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', f, { passive: true });
    return () => window.removeEventListener('scroll', f);
  }, []);
  useEffect(() => {
    const f = () => setVw(window.innerWidth);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);

  const lines = t(h.title).split('\n');
  const branchNames = c.branches.map(x => t(x.name).replace(/^Glamm\s+/i, '')).join(' · ');

  // Chip cards around the cup — admin-configurable via hero.signatureChip / hero.freshChip
  const sigCfg = h.signatureChip || { show: true };
  const freshCfg = h.freshChip || { show: true };
  const signatureItem = sigCfg.show === false ? null : (
    (sigCfg.menuId != null && c.menu.items.find(i => i.id === sigCfg.menuId))
    || c.menu.items.find(i => i.tag === 'Signature')
    || c.menu.items[0]
  );
  const freshItem = freshCfg.show === false ? null : (
    (freshCfg.menuId != null && c.menu.items.find(i => i.id === freshCfg.menuId))
    || c.menu.items.find(i => i.tag === 'New' || i.cat === 'kahvalti' || i.cat === 'tatli')
    || c.menu.items[1]
  );
  const sigLabel = (sigCfg.labelOverride?.tr || sigCfg.labelOverride?.en)
    ? sigCfg.labelOverride
    : null;
  const freshLabel = (freshCfg.labelOverride?.tr || freshCfg.labelOverride?.en)
    ? freshCfg.labelOverride
    : null;

  return (
    <section id="top" className="relative min-h-[100svh] overflow-hidden bg-cream grain">
      <div className="orb float-slow"     style={{ width: 560, height: 560, left: '-12%',   top: '-12%',  background: 'radial-gradient(circle, #f5dcd4 0%, transparent 60%)' }}/>
      <div className="orb float-slow-alt" style={{ width: 660, height: 660, right: '-16%',  top: '8%',    background: 'radial-gradient(circle, #ede0ce 0%, transparent 60%)' }}/>
      <div className="orb float-slow"     style={{ width: 460, height: 460, left: '32%',    bottom: '-24%', background: 'radial-gradient(circle, #e9b7a6 0%, transparent 60%)', opacity: 0.5 }}/>

      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(201,165,117,0.08) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(233,183,166,0.06) 0, transparent 40%)',
      }}/>

      {h.showBeans && <FloatingBeans count={h.beanCount}/>}

      <div className="relative mx-auto max-w-7xl px-5 md:px-6 pt-28 md:pt-36 pb-10">
        <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center mt-4 md:mt-10">
          <div className="md:col-span-6 relative z-10">
            <Reveal delay={1}>
              <h1 className="font-display text-[40px] sm:text-[48px] md:text-[88px] leading-[0.98] md:leading-[0.95] tracking-tight text-espresso">
                {lines.map((l, i) => (
                  <span key={i} className="block">
                    {i === lines.length - 1
                      ? <em className="italic text-cocoa font-medium gradient-sheen">{l}</em>
                      : l}
                  </span>
                ))}
              </h1>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 text-[15.5px] md:text-[17px] leading-relaxed text-espresso/75 max-w-[520px]">
                {t(h.sub)}
              </p>
            </Reveal>
            <Reveal delay={3}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button onClick={onOpenMenu}
                  className="btn-primary group inline-flex items-center gap-2 rounded-full bg-espresso text-cream px-6 py-3.5 text-[13.5px] tracking-wide hover:bg-ink transition-all">
                  {t(h.ctaPrimary)}
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </button>
                <button onClick={onOpenStores}
                  className="inline-flex items-center gap-2 rounded-full border border-espresso/20 px-6 py-3.5 text-[13.5px] tracking-wide hover:bg-white/50 transition-all">
                  {t(h.ctaSecondary)}
                </button>
              </div>
            </Reveal>
            <Reveal delay={4}>
              <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 text-[11.5px] tracking-wide text-espresso/60">
                <div className="flex -space-x-2">
                  {['rose','latte','pistachio'].map((t,i)=>(
                    <div key={i} className="h-8 w-8 rounded-full border-2 border-cream" style={{ background: TONE_BG[t] }}/>
                  ))}
                </div>
                <div>
                  <div className="font-display text-[20px] text-espresso leading-none">
                    <Counter value={h.rating} duration={1800}/> ★
                  </div>
                  <div className="mt-1">{t(h.ratingLabel)}</div>
                </div>
                <div className="hidden md:block h-8 w-px bg-espresso/10"/>
                <div>
                  <div className="font-display text-[20px] text-espresso leading-none">
                    <Counter value={h.stat} duration={1600}/>
                  </div>
                  <div className="mt-1">{t(h.statLabel)}</div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6 relative min-h-[440px] md:min-h-[620px] flex items-center justify-center">
            <div className="relative" style={{
              transform: `translate3d(${parallaxX * -14}px, ${parallaxY * -10 + scrollY * 0.08}px, 0) rotate(${parallaxX * 1}deg)`,
              transition: 'transform 0.6s cubic-bezier(.2,.6,.2,1)',
            }}>
              <div className="absolute -inset-16 md:-inset-24 rounded-full pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, rgba(245,220,212,0.75) 0%, rgba(245,220,212,0.25) 42%, rgba(245,220,212,0) 72%)',
                  filter: 'blur(6px)',
                }}/>
              <LatteCup size={cupSize} speed={h.cupSpeed} showLogo={h.showLogoOnCup} style={h.cupStyle} logoSrc={c.brand?.logo || '/assets/glamm.png'}/>

              {signatureItem && (
                <Reveal delay={3}>
                  <div className="absolute left-0 md:-left-10 top-8 md:top-20 glass rounded-2xl px-3 md:px-4 py-2 md:py-3 shadow-[0_10px_40px_rgba(74,46,30,0.12)] tilt-card max-w-[150px] md:max-w-none"
                    style={{ transform: `translate(${parallaxX * 8}px, ${parallaxY * 8}px)` }}>
                    <div className="font-mono text-[8.5px] md:text-[9px] tracking-[0.2em] uppercase text-tan">
                      {sigLabel ? t(sigLabel) : (signatureItem.tag || t(UI.hero.signature))}
                    </div>
                    <div className="font-display text-[13px] md:text-[16px] text-espresso mt-0.5">{t(signatureItem.name)}</div>
                    <div className="text-[10.5px] md:text-[11px] text-espresso/60 mt-0.5 max-w-[130px] md:max-w-[180px] line-clamp-1">{t(signatureItem.note)}</div>
                  </div>
                </Reveal>
              )}
              {freshItem && (
                <Reveal delay={4}>
                  <div className="absolute right-0 md:-right-10 bottom-8 md:bottom-20 glass rounded-2xl px-3 md:px-4 py-2 md:py-3 shadow-[0_10px_40px_rgba(74,46,30,0.12)] tilt-card max-w-[150px] md:max-w-none"
                    style={{ transform: `translate(${parallaxX * -8}px, ${parallaxY * -8}px)` }}>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-600/80 animate-pulse"/>
                      <div className="font-mono text-[8.5px] md:text-[9px] tracking-[0.2em] uppercase text-cocoa">{freshLabel ? t(freshLabel) : t(UI.hero.freshNow)}</div>
                    </div>
                    <div className="font-display text-[13px] md:text-[16px] text-espresso mt-0.5">{t(freshItem.name)}</div>
                    <div className="text-[10.5px] md:text-[11px] text-espresso/60 mt-0.5 max-w-[130px] md:max-w-[180px] line-clamp-1">{t(freshItem.note)}</div>
                  </div>
                </Reveal>
              )}

              <Reveal delay={2}>
                <div className="absolute right-2 md:-right-2 top-2 md:top-10 glass rounded-full px-3 py-1.5 shadow-[0_6px_24px_rgba(74,46,30,0.1)] flex items-center gap-1.5"
                  style={{ transform: `translate(${parallaxX * -6}px, ${parallaxY * 6}px)` }}>
                  <span className="text-[13px]">⭐</span>
                  <span className="font-display text-[13px] text-espresso">{h.rating}</span>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3 text-[10.5px] font-mono tracking-[0.25em] uppercase text-espresso/50">
          <div className="h-px w-10 bg-espresso/20"/>
          <span>{t(UI.hero.scrollDown)}</span>
          <span className="scroll-dot"/>
        </div>
      </div>

      <div className="relative overflow-hidden py-4 md:py-5 border-y border-espresso/10 bg-latte/40 mt-8 md:mt-10">
        <div className="marquee-track flex gap-8 md:gap-12 whitespace-nowrap font-display text-[22px] md:text-[46px] text-espresso/70 italic">
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="flex gap-12 items-center">
              <span>Rose Latte</span><span className="text-tan">✦</span>
              <span>Pistachio Mocha</span><span className="text-tan">✦</span>
              <span>Butter Croissant</span><span className="text-tan">✦</span>
              <span>Basque Cheesecake</span><span className="text-tan">✦</span>
              <span>Cardamom Bun</span><span className="text-tan">✦</span>
              <span>Espresso Tonic</span><span className="text-tan">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
